import { Box, Checkbox, FormControl, FormLabel, Grid, ListItemText, MenuItem, Select, Skeleton, Typography } from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import PropTypes from "prop-types";
import _ from "lodash";
import { styled } from "@mui/system";
import ErrorBox from "../shared/ErrorBox";

export const StyledSelect = styled(Select, {
  shouldForwardProp: (prop) => prop !== "error",
})(({ error }) => ({
  backgroundColor: "white",

  "& .MuiOutlinedInput-root": {
    height: "45px",
    padding: "0px",
    alignItems: "center",
    boxSizing: "border-box",

    "& fieldset": {
      border: error ? "1px solid #C75C57" : "1px solid silver",
    },

    "&:hover fieldset": {
      border: error ? "1px solid #C75C57" : "1px solid black",
    },

    "&.Mui-focused fieldset": {
      border: error ? "1px solid #C75C57" : "1px solid black",
    },
  },

  "& .MuiSelect-select": {
    height: "44.5px",
    display: "flex",
    alignItems: "center",
    padding: "10px 12px", // 👈 Y-axis padding
    boxSizing: "border-box",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  "& .MuiSvgIcon-root": {
    height: "25px",
  },
}));

const MultiSelectField = ({ field, control, errors, isLoading }) => {
  const selectedValues = useWatch({ control, name: field.name }) || [];

  const getFormLabel = () => (
    <FormLabel
      component="legend"
      sx={{
        mb: 2,
        ml: {
          xs: 0,
          sm: 0,
          md: field.leftMargin || 0,
        },
      }}>
      <Typography variant="h6">
        {field.label}
        {field.isRequired && <span>*</span>}
      </Typography>
      <Typography variant="body1" component="span">
        {field.helpText}
      </Typography>
    </FormLabel>
  );

  const getFormField = () => (
    <Box>
      <Controller
        control={control}
        name={field.name}
        defaultValue={[]}
        render={({ field: controllerField }) => (
          <StyledSelect
            fullWidth
            multiple
            error={!!_.get(errors, field.name)}
            {...controllerField}
            value={controllerField.value || []}
            renderValue={(selected) =>
              field.values
                .filter((item) => selected.includes(item.value))
                .map((item) => item.label)
                .join(", ")
            }>
            {field.values.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selectedValues.includes(option.value)} />
                <ListItemText primary={option.label} sx={{ textAlign: "left" }} />
              </MenuItem>
            ))}
          </StyledSelect>
        )}
      />
      <ErrorBox message={_.get(errors, field.name)?.message} showIcon={false} />
    </Box>
  );

  return (
    <FormControl fullWidth>
      {isLoading ? (
        <Skeleton animation="wave" />
      ) : field?.options?.sameRow ? (
        <Grid container>
          <Grid item xs={4}>
            {getFormLabel()}
          </Grid>
          <Grid item xs={8}>
            {getFormField()}
          </Grid>
        </Grid>
      ) : (
        <Box>
          {getFormLabel()}
          {getFormField()}
        </Box>
      )}
    </FormControl>
  );
};

MultiSelectField.propTypes = {
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    helpText: PropTypes.string,
    isRequired: PropTypes.bool,
    multiple: PropTypes.bool,
    values: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ).isRequired,
    options: PropTypes.shape({
      sameRow: PropTypes.bool,
    }),
    leftMargin: PropTypes.string,
  }).isRequired,
  control: PropTypes.shape({
    register: PropTypes.func,
    unregister: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    trigger: PropTypes.func,
  }).isRequired,
  errors: PropTypes.oneOfType([PropTypes.array, PropTypes.objectOf(PropTypes.any)]).isRequired,
  isLoading: PropTypes.bool,
};

MultiSelectField.defaultProps = {
  isLoading: false,
};

export default MultiSelectField;
